import router from '../../router/index'
import { needWantDefaultList } from '../../components/client/account/feedbackNeedsWants'
import {
  feedbackNeededProperties,
  feedbackProvidedProperties
} from '../../utils/propertyUtils'
import { getPropertyById } from '../../services/propertiesService'
import {
  getAllMessages,
  markMessageAsRead,
  calculateThreadsFromMessages
} from '../../services/messagesService'
import { createCalendarTimeFrame } from '../../services/routeService'
import { isEmpty } from '../../utils/generalUtils'

let interval

const getDefaultClientState = () => {
  return {
    clientData: null,
    clientHomes: null,
    clientDialogs: [],
    clientMessages: [],
    associatedAgent: null,
    associatedAgentDataReady: false,
    selectedDialogUser: null,
    clientFavorites: [],
    clientRouteData: [],
    preferenceSuggestions: [],
    clientProvideFeedback: null,
    clientChanged: true,
    savedFavWidgetRequestTracker: false,
    sortedPropertyIds: [],
    assignToClient: {},
    customText: false
  }
}

const state = getDefaultClientState()

const getters = {
  getInitialPrefSuggestions: state => {
    if (state.clientData && state.clientData.homePrefs) {
      return needWantDefaultList.filter(sourceItem => {
        let isPresent = false
        state.clientData.homePrefs.options.forEach(objectItem => {
          if (sourceItem.text === objectItem.text) {
            isPresent = true
          }
        })
        return !isPresent
      })
    }
    return needWantDefaultList
  },

  clientPreferences: state => preference => {
    if (state.clientData && state.clientData.homePrefs) {
      const prefs = state.clientData.homePrefs.options.map(item => {
        return {
          match: false,
          name: item.text,
          wantNeed: item.wantNeed
        }
      })
      if (!preference) return prefs
      return prefs.filter(pref => pref.wantNeed === preference)
    }
    return []
  },

  clientHomePrefs: state => preference => {
    if (state.clientData && state.clientData.homePrefs) {
      return state.clientData.homePrefs.options.filter(
        pref => pref.wantNeed === preference
      )
    }
    return []
  },

  feedbackNeededHomes: state => {
    if (state.clientData) {
      return feedbackNeededProperties(
        state.clientData.homes,
        state.clientData.routes
      )
    }
    return []
  },

  feedbackProvidedHomes: state => {
    if (state.clientData) {
      return feedbackProvidedProperties(state.clientData.homes)
    }
    return []
  },

  clientUnreadMessages: state => {
    if (state.clientData && state.clientMessages) {
      return state.clientMessages.filter(
        message => !message.read && message.from !== state.clientData._id
      )
    }
    return []
  },

  getAssociatedLogo: state => {
    return state.associatedAgent
      ? state.associatedAgent.media.find(item => item.type === 'logo')
      : undefined
  }
}

const actions = {
  unsubscribe() {
    if (interval) {
      clearInterval(interval)
    }
  },
  async getDialogsUsers({ commit, state }) {
    if (state.clientData && state.clientMessages) {
      let dialogUsers = []
      state.clientMessages.forEach(message => {
        if (
          dialogUsers.indexOf(message.from) === -1 &&
          message.from !== state.clientData._id
        ) {
          dialogUsers.push(message.from)
        }
      })

      let userData = []
      userData = [state.clientData, ...state.clientData.agents]
      dialogUsers = userData.map(sender => {
        const unreadMessages = state.clientMessages
          .filter(
            message => !message.read && message.from !== state.clientData._id
          )
          .filter(message => message.from === sender._id)
        return { ...sender, ...{ unreadMessages: unreadMessages } }
      })
      commit('SET_DIALOGS', dialogUsers)
    }
  },
  /**
   * Action to clear login status of agent side
   */
  clientLogout({ commit }) {
    if (localStorage.getItem('x-authentication')) {
      localStorage.removeItem('x-authentication')
      localStorage.removeItem('profileStatus')
    }
    commit('common/SET_LOGIN_STATUS', {
      status: false,
      message: null,
      error: false
    })
    commit('common/SET_PROFILE_STATUS', null, { root: true })
    router.push({ name: 'login' })
  },

  /**
   * Retrieves the property details by sending the home id
   * in the request body
   *
   * @param {Object} clientObject - searched client
   */
  async clientHomeDetailsAction(
    { commit, rootState, state },
    { _id, homes, partial }
  ) {
    if (_id) {
      let toBeLoadedHomes = [...homes]
      let loadedHomes = []
      if (partial) {
        toBeLoadedHomes = toBeLoadedHomes.filter(
          home => home.requestTour !== true
        )
        const favHomes = rootState.agent.propertiesData.find(
          data => data.clientId === _id
        )
        loadedHomes = [...favHomes.propertyData]
      } else {
        commit('CLEAR_CLIENT_FAVORITES')
      }

      let propertyData = []
      const homeRequests = toBeLoadedHomes.map(async home => {
        if (home.propertyId) {
          const response = await getPropertyById(home.propertyId)
          if (response.data) {
            propertyData.push({
              ...response.data,
              ...home,
              favoriteId: home._id,
              _id: response.data._id
            })
            return response.data
          }
        }
        return false
      })

      Promise.all(homeRequests).then(res => {
        if (res) {
          propertyData = [...propertyData, ...loadedHomes]
          commit('SAVE_CLIENT_FAVORITES', propertyData)
          commit('SET_CLIENT_CHANGED', true)
          commit(
            'agent/SET_PROPERTIES_DATA',
            { clientId: _id, propertyData },
            { root: true }
          )
        }
      })
    }
  },

  // Action to update client homes and favorites
  updateClientPropertiesAction(
    { commit, state, rootState },
    { updatedClientHomes, updatedClientFavs }
  ) {
    commit('SET_CLIENT_DATA', {
      ...state.clientData,
      homes: updatedClientHomes
    })
    commit('SAVE_CLIENT_FAVORITES', updatedClientFavs)
    if (rootState.common.profileStatus === 'agent') {
      commit(
        'agent/UPDATE_PROPERTIES_DATA',
        {
          clientId: state.clientData._id,
          propertyData: updatedClientFavs
        },
        { root: true }
      )
    }
  },

  clientRouteDataAction({ commit }, data) {
    let routeArray = []
    if (data.routes && data.routes.length > 0) {
      data.routes.forEach(route => {
        return routeArray.push({
          ...route,
          clientId: data._id,
          clientName: data.name,
          ...createCalendarTimeFrame(route)
        })
      })
    }
    commit('SET_CLIENT_ROUTE_DATA', routeArray)
    commit('common/SET_LOADING_ROUTES', false, { root: true })
  },

  async fetchClientMessagesAction({ commit, dispatch, state }) {
    const res = await getAllMessages()
    commit('SET_CLIENT_MESSAGES', res)
    dispatch('communication/setThreads', calculateThreadsFromMessages(res), {
      root: true
    })
    dispatch('getDialogsUsers')
    dispatch('fetchClientMessagesIntervalAction')
  },

  async fetchClientMessagesIntervalAction({ commit, dispatch }) {
    if (interval) return
    interval = setInterval(async () => {
      const res = await getAllMessages()
      commit('SET_CLIENT_MESSAGES', res)
      dispatch('communication/setThreads', calculateThreadsFromMessages(res), {
        root: true
      })
      dispatch('getDialogsUsers')
    }, 10000)
  },

  async markMessagesAsRead({ commit }, data) {
    await Promise.all(
      data.messages.map(message => markMessageAsRead(message._id))
    )
    commit('MARK_CLIENT_MESSAGE_AS_READ', data.user)
  },

  updateSingleFavorite({ commit, state }, favorite) {
    const index = state.clientFavorites.findIndex(item => {
      return item.favoriteId === favorite.favoriteId
    })
    const updatedFavorites = [...state.clientFavorites]
    updatedFavorites[index] = favorite
    commit('SAVE_CLIENT_FAVORITES', updatedFavorites)
  }
}

const mutations = {
  SET_ASSIGN_TO_CLIENT: (state, client) => {
    if (isEmpty(client)) {
      state.customText = false
    }
    state.assignToClient = client
  },

  SET_CUSTOM_TEXT: (state, status) => {
    state.customText = status
  },

  SET_DIALOGS: (state, payload) => {
    state.clientDialogs = payload
  },

  SET_SAVED_FAV_WIDGET_REQUEST_TRACKER: (state, status) => {
    state.savedFavWidgetRequestTracker = status
  },

  SET_SORTED_PROPERTY_IDS: (state, idArray) => {
    state.sortedPropertyIds = idArray
  },

  SET_DIALOG_USER: (state, payload = null) => {
    state.selectedDialogUser = payload
  },

  SET_CLIENT_DATA: (state, payload) => {
    state.clientData = payload
    if (payload) {
      state.clientHomes = payload.homes
    }
  },

  SET_CLIENT_MESSAGES: (state, payload) => {
    state.clientMessages = payload
  },

  SET_ASSOCIATED_AGENT: (state, payload) => {
    state.associatedAgent = payload
    state.associatedAgentDataReady = !!payload
  },

  CLEAR_CLIENT_FAVORITES: state => {
    if (state.clientFavorites) {
      state.clientFavorites = []
    }
  },

  SAVE_CLIENT_FAVORITES: (state, homeDetails) => {
    state.clientFavorites = homeDetails
  },

  UPDATE_CLIENT_FAVORITES: (state, { updatedFavorites, updatedHomes }) => {
    state.clientFavorites = updatedFavorites
    state.clientHomes = updatedHomes
  },

  SET_CLIENT_ROUTE_DATA: (state, routeData) => {
    state.clientRouteData = routeData
  },

  SET_CLIENT_CHANGED: (state, status) => {
    state.clientChanged = status
  },

  SET_PREFERENCE_LIST: state => {
    state.preferenceSuggestions =
      state.clientData && state.clientData.homePrefs
        ? needWantDefaultList.filter(sourceItem => {
          let isPresent = false
          state.clientData.homePrefs.options.forEach(objectItem => {
            if (sourceItem.text === objectItem.text) {
              isPresent = true
            }
          })
          return !isPresent
        })
        : needWantDefaultList
  },

  UPDATE_PREFERENCE_LIST: (state, { value, step }) => {
    if (step === 'add') {
      const prefIndex = state.preferenceSuggestions.findIndex(
        pref => pref.text === value
      )
      state.preferenceSuggestions.splice(prefIndex, 1)
    } else if (step === 'remove') {
      state.preferenceSuggestions.push(value)
    }
  },

  MARK_CLIENT_MESSAGE_AS_READ: (state, selectedUser) => {
    state.clientDialogs = state.clientDialogs.map(user => {
      if (user._id === selectedUser._id) {
        user.unreadMessages.forEach(message => {
          message.read = true
        })
        user.unreadMessages = []
      }
      return user
    })
  },

  SET_CLIENT_PROVIDE_FEEDBACK: (state, propertyId) => {
    state.clientProvideFeedback = propertyId
  },

  SET_DEFAULT_CLIENT_STATE: state => {
    Object.assign(state, getDefaultClientState())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
